import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import useDatasources from '@/hooks/useDatasources';
import { closeDeleteDatasourceModal } from '@/redux/collections/actions';
import {
  selectDeleteDatasourceTargetId,
  selectIsDeleteDatasourceModalOpen,
} from '@/redux/collections/selectors';
import { setDatasourceId } from '@/redux/session/actions';
import { selectDatasourceId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';

import { useTabCategory } from '../hooks/useCategory';

export const DeleteDatasourceModal = () => {
  // Redux state
  const { collectionId } = useParams();
  // The row the menu was opened on, not the row a right panel is pointing at.
  const datasourceId = useSelector(selectDeleteDatasourceTargetId);
  const selectedDatasourceId = useSelector(selectDatasourceId);
  const open = useSelector(selectIsDeleteDatasourceModalOpen);
  const dispatch = useDispatch();

  // Custom hooks
  const { t } = useTranslation();
  const { category } = useTabCategory();
  const { deleteDatasource, deleteStatus, datasource } = useDatasources(collectionId, datasourceId);

  // Local states
  const name = datasource?.name;
  const isDeleting = deleteStatus === 'pending';

  const getTitleTranslation = () => {
    switch (category) {
      case 'faq':
        return t('collections.modals.delete_faq_title');
      case 'documents':
        return t('collections.modals.delete_document_title');
      case 'webpages':
        return t('collections.modals.delete_webpage_title');
      case 'external':
        return t('collections.modals.delete_external_title');
    }
  };

  const getSubtitleTranslation = () => {
    switch (category) {
      case 'faq':
        return t('collections.modals.delete_faq_subtitle');
      case 'documents':
        return t('collections.modals.delete_document_subtitle');
      case 'webpages':
        return t('collections.modals.delete_webpage_subtitle');
      case 'external':
        return t('collections.modals.delete_external_subtitle');
    }
  };

  const handleDelete = () => {
    deleteDatasource(datasourceId, {
      onSuccess: () => {
        dispatch(closeDeleteDatasourceModal());
        // Only the panel showing the deleted folder is stale; one open on another folder keeps
        // its selection.
        if (selectedDatasourceId === datasourceId) {
          dispatch(setDatasourceId(null));
          dispatch(setSidebarContent(null));
        }
      },
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      dispatch(closeDeleteDatasourceModal());
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="gap-6 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{getTitleTranslation()}</DialogTitle>
          <DialogDescription>
            <Trans i18nKey={getSubtitleTranslation()} values={[name]} />
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end gap-2">
          <Button variant="ghost-neutral" onClick={() => dispatch(closeDeleteDatasourceModal())}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            className="text-destructive"
            onClick={handleDelete}
            isLoading={isDeleting}
          >
            {t('common.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
